import React, { useState } from 'react';
import * as Sentry from '@sentry/browser';
import classNames from 'classnames';
import moment from 'moment';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { ensureTransaction } from '../../util/data';
import {
  txRoleIsCustomer,
  txIsDelivered,
  getUserTxRole,
  txRoleIsProvider,
} from '../../util/transaction';

import SendMessageSection from './SendMessageSection/SendMessageSection';
import ActionButtons from './ActionButtons/ActionButtons';
import ReviewModal from './ReviewModal/ReviewModal';
import PartialCancelModal from './PartialCancelModal/PartialCancelModal';
import DeliveryStateModal from './DeliveryStateModal/DeliveryStateModal';
import ProlongationModal from './ProlongationModal/ProlongationModal';
import ServiceFeeModal from './ServiceFeeModal/ServiceFeeModal';
import PickupModal from './PickupModal/PickupModal';
import UpdateReceiverModal from './UpdateReceiverModal/UpdateReceiverModal';
import {
  InlineTextButton,
  UserDisplayName,
  IconArrowHead,
  NamedLink,
  Button,
  SecondaryButton,
  OrderItemInfo,
  RelatedTransactionsSection,
  OrderDetails,
  FeedSection,
  LogoMedicoverSport,
  WarningComponent,
  PrimaryButton
} from '..';
import { setOrderedProducts } from './setOrderedProducts';

import css from './TransactionPanel.css';
import TransactionsModal from './TransactionsModal/TransactionsModal';
import ProtocolState from './ProtocolState/ProtocolState';

const transactionStatus = [
  { status: 'payment-expired', textStatus: 'DetailOrder.paymentExpired', class: css.expired },
  { status: 'pending-payment', textStatus: 'DetailOrder.pendingPayment', class: css.pending },
  { status: 'accepted', textStatus: 'DetailOrder.inProgress', class: css.inProgress },
  { status: 'picked-up', textStatus: 'DetailOrder.inProgress', class: css.inProgress },
  { status: 'cancelled', textStatus: 'DetailOrder.cancelled', class: css.cancelled },
  { status: 'delivered', textStatus: 'DetailOrder.delivered', class: css.delivered },
  { status: 'declined', textStatus: 'DetailOrder.cancelled', class: css.cancelled },
];

const canCustomerCancel = (tx) => {
  if(!tx.attributes.isCancellableByUser) return false;
  const providerCountryId = tx.provider.attributes.countryId;
  if (providerCountryId === 'pl') return false;
  if (tx.attributes.processState !== 'accepted') return false;

  return !!tx.attributes.isCancellableByUser;
}

const TransactionPanelComponent = ({
  transaction,
  intl,
  transactionRole,
  acceptInProgress,
  declineInProgress,
  pickupInProgress,
  cancelInProgress,
  completeNotPickedupInProgress,
  completeInProgress,
  acceptSaleError,
  declineSaleError,
  pickupSaleError,
  completeSaleError,
  onAcceptSale,
  onDeclineSale,
  onPickupSale,
  onCompleteNotPickedupSale,
  onCompleteSale,
  onCancelSale,
  sendReviewError,
  sendReviewInProgress,
  onManageDisableScrolling,
  onSendReview,
  review,
  currentUser,
  currentUserHasListings,
  currentUserProviderId,
  fetchMessagesError,
  fetchMessagesInProgress,
  initialMessageFailed,
  oldestMessagePageFetched,
  messages,
  totalMessagePages,
  onShowMoreMessages,
  sendMessageInProgress,
  sendMessageError,
  onSendMessage,
  partialCancelInProgress,
  partialCancelError,
  onPartialCancelOrder,
  partialCancelSuccess,
  pageName,
  onUpdateTransaction,
  reviewNeeded,
  onChangeDeliveryState,
  changeDeliveryStateInProgress,
  canBeProlongedTo,
  callSetInitialValues,
  onInitializeCardPaymentData,
  history,
  createServiceFeeInProgress,
  createServiceFeeError,
  createServiceFeeSuccess,
  onCreateServiceFee,
  onClearServiceFee,
  transactionSuccessComponent,
  completedTxs,
  ongoingTxs,
  futureTxs,
  overdueTxs,
  notPaidServiceFeesTxs,
  onUpdateProtocolInfo,
  onSignProtocol,
  signProtocolInProgress,
  signProtocolSuccess,
  signProtocolError,
  onUpdateTransactionOnly,
  onUpdateReceiver,
  duplicatedPeselTransactions,
  onUpdateStolenStatus,
  markAsStolenInProgress,
  markAsStolenError
}) => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(!!reviewNeeded);
  const [isPartialCancelModalOpen, setIsPartialCancelModalOpen] = useState(false);
  const [isDeliveryStateModalOpen, setIsDeliveryStateModalOpen] = useState(false);
  const [isProlongationModalOpen, setIsProlongationModalOpen] = useState(false);
  const [isChargeServiceFeeModalOpen, setIsChargeServiceFeeModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [isTransactionsModalOpen, setIsTransactionsModalOpen] = useState(false);
  const [isPeselModalOpen, setIsPeselModalOpen] = useState(false);
  const [isUpdateReceiverModalOpen, setIsUpdateReceiverModalOpen] = useState(false);
  const [protocolType, setProtocolType] = useState('pickup');

  const [txType, setTxType] = useState(null);
  const providerName = transaction.provider.attributes.name;
  const listings = transaction.listings;
  const isCustomerLoaded = !!transaction.user.id;
  const isCustomerBanned = isCustomerLoaded && transaction.user.attributes.banned;
  const isProvider = transactionRole === 'provider';
  const ownRole = getUserTxRole(currentUser.id, transaction);
  const { processState: status, lineItems, transitions } = transaction.attributes;
  const isAdmin = currentUser.attributes.isAdmin;
  const isLocalAdmin = currentUser.attributes.adminForCountries?.includes(transaction.provider.attributes.countryId);
  const showSaleButtons = ['request', 'accepted', 'picked-up', 'picked-up', 'delivered'].includes(status)
    ? isProvider && (!isCustomerBanned || isAdmin || isLocalAdmin)
    : null;

  const customer = transaction.user;
  const provider = transaction.provider;
  const { countryId, isDecathlon } = provider.attributes;
  const displayProtocolPrinter = isProvider && isDecathlon && countryId === 'pl';

  const displayName = txRoleIsProvider(ownRole) ? (
    <UserDisplayName user={customer} intl={intl} nameToDisplay={customer.attributes.displayName} />
  ) : (
    <UserDisplayName user={provider} intl={intl} nameToDisplay={provider.attributes.name} />
  );
  const isServiceFee = !!transaction.attributes.serviceFee;

  const expiredProducts = status === "payment-expired" || status === "pending-payment" ? lineItems.filter(item => item?.code === 'day' && !item.reversal) : null

  const actualOrderedProducts = !isServiceFee ?
    (lineItems?.length && transitions?.length && setOrderedProducts(lineItems, transitions)) : [];

  const sumSeats = !!actualOrderedProducts && actualOrderedProducts.reduce((accumulator, { seats }) => {
    accumulator += seats;
    return accumulator;
  }, 0);

  const isPartialCancelButton =
    ['accepted', 'picked-up'].includes(status) && isProvider && sumSeats > 1 && !transaction?.attributes?.partnership;

  const createdAt = transaction?.attributes?.createdAt;
  const isWithin14DaysHu = createdAt
    ? moment().isBefore(moment(createdAt).startOf('day').add(14, 'days').endOf('day'))
    : false;

  const showHuExternalFormBtn = countryId === 'hu' && isDecathlon && status === 'picked-up' && isWithin14DaysHu;

  const closeRateModal = () => {
    setIsReviewModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const openRateModal = () => {
    setIsReviewModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeTransactionsModal = () => {
    setIsTransactionsModalOpen(false);
    setTxType(null);
  };

  const openTransactionsModal = (type) => {
    setIsTransactionsModalOpen(true);
    setTxType(type)
  }

  const openPeselModal = (type) => {
    setIsPeselModalOpen(true);
    setProtocolType(type);
  };

  const closePeselModal = () => setIsPeselModalOpen(false);

  const handleSignProtocol = async (file, page) => {
    const key = page === 'pickup' ? transaction.attributes.protocol.pickupSignKey : transaction.attributes.protocol.deliverSignKey;

    try {
      await onSignProtocol({
        txId: transaction.id.uuid,
        file,
        page,
        key
      })
    } catch (err) {
      Sentry.withScope((scope) => {
        Sentry.captureException(err);
      });
    }
  }

  const onSubmitPeselData = ({ pesel, personalNumberType, ...remarksData }) => {
    const inspectionNotes = Object.entries(remarksData.remarks).map(([key, value]) => {
      const listingId = key.replace('remarks-', '');
      return {
        listingId,
        note: value,
      };
    });

    return onUpdateProtocolInfo({
      txId: transaction.id.uuid,
      personalNumber: pesel,
      personalNumberType,
      inspectionNotes,
      protocolType
    })
      .then((res) => {
        return res
      })
      .catch((e) => {
        console.error('Błąd podczas aktualizacji danych protokołu:', e);
      });
  };

  const openProlongationModal = () => {
    setIsProlongationModalOpen(true);
  }

  const closeProlongationModal = () => {
    setIsProlongationModalOpen(false);
  }

  const closePartialCancelModal = () => {
    setIsPartialCancelModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const openPartialCancelModal = () => {
    setIsPartialCancelModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeDeliveryStateModal = () => {
    setIsDeliveryStateModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const openDeliveryStateModal = () => {
    setIsDeliveryStateModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeProlongationModalOpen = () => {
    setIsProlongationModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const openServiceFeeModal = () => {
    setIsChargeServiceFeeModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeServiceFeeModal = () => {
    setIsChargeServiceFeeModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const onSubmitReview = values => {
    const currentTransaction = ensureTransaction(transaction);
    const { reviewRating, reviewContent } = values;
    const rating = Number.parseInt(reviewRating, 10);
    onSendReview(transactionRole, currentTransaction, rating, reviewContent)
      .then(response => {
        closeRateModal();
        setReviewSubmitted(true);
      })
      .catch(e => {
        // Do nothing.
      });
  };

  const protocolFile = transaction.attributes.documents.find(d => d.type === 'protocol');
  const isRelatedTransactions = transaction && !!transaction.relatedTransactions.length;

  const computeDeliveryState = (deliveryState, deliveryType) => {
    const isHome = deliveryType === 'home';
    if (deliveryState === 'not-delivered') return isHome ? 'oczekuje na wysyłkę do klienta' : 'oczekuje na wysyłkę do sklepu';
    if (deliveryState === 'sent-to-store') return isHome ? 'wysłano do klienta' : 'wysłano do sklepu';
    if (deliveryState === 'received-by-store') return isHome ? 'odebrano przez klienta' : 'odebrano w sklepie';
    if (deliveryState === 'sent-to-stock') return 'wysłano do magazynu';
    if (deliveryState === 'received') return 'odebrano w magazynie';
  }

  const isExtendRental = transaction.relatedTransactions.find(t => {
    const transactionEnd = moment(transaction.attributes.end);
    const prolongStart = moment(t.attributes.start);

    return t.attributes.state !== "declined" && moment(transactionEnd).isSame(prolongStart)
  });

  const isExtendRentalBtn = txRoleIsCustomer(ownRole)
    && canBeProlongedTo
    && canBeProlongedTo.length > 0
    && !isExtendRental;

  const transactionStatusAttributes = transactionStatus.find(
    (item) => item.status === status
  );

  const HOURS_OF_SERVICE_FEE_BUTTON_AFTER_COMPLETE = 72;
  const completeProcessState = transaction.attributes.transitions.find(item => item.transition === 'complete');
  const endDateDisplaysServiceFee = moment(new Date(completeProcessState?.createdAt)).add(HOURS_OF_SERVICE_FEE_BUTTON_AFTER_COMPLETE, 'hours');
  const isBeforeEndDateDisplaysServiceFee = moment().isBefore(endDateDisplaysServiceFee)

  const medicoverPartnership = transaction.attributes.partnership;
  const isPartnershipWarning = transaction.attributes.partnership
    && transitions.length === 1
    && transitions[0].transition === "paymentless-accept";
  const partnershipWarningMessage = isProvider
    ? "TransactionPanel.salePartnershipWarning"
    : "TransactionPanel.orderPartnershipWarning";

  const userWarning = transaction?.provider?.attributes.isDecathlon;

  const completeTransition = transaction.attributes.transitions.find(
    t => t.transition === 'complete'
  );

  const isLessThanOneDay = completeTransition
    ? moment().diff(moment(completeTransition.createdAt), 'hours') <= 24
    : true;

  const isProtocolButton =
    !['cancelled', 'payment-expired'].includes(status) && isProvider && displayProtocolPrinter && isLessThanOneDay;

  const { deliveryState, deliveryType } = transaction.attributes;

  const isCancelByCustomerBtn = !isProvider && canCustomerCancel(transaction);

  return (
    <div className={css.container}>
      {transactionSuccessComponent}
      <div className={css.navigation}>
        <NamedLink
          className={classNames(css.text, css.link)}
          name="InboxPage"
          params={{
            tab: isProvider ? 'sales' : 'orders',
            providerId: currentUserProviderId || 'orders',
          }}
        >
          <FormattedMessage id="DetailOrder.booking" />
        </NamedLink>
        <IconArrowHead direction={'right'} className={css.arrow} />
        <span className={css.text}>
          <FormattedMessage id="DetailOrder.orderFrom" values={{ providerName }} />
        </span>
      </div>
      {isPartnershipWarning && <WarningComponent message={partnershipWarningMessage} />}
      <div className={css.wrapper}>
        <OrderDetails
          providerName={providerName}
          listings={listings}
          intl={intl}
          actualOrderedProducts={expiredProducts ? expiredProducts : actualOrderedProducts}
          transaction={transaction}
          codeMessage="OrderDetails.bookingNo"
          titleMessage="DetailOrder.orderFrom"
          transactionStatusAttributes={transactionStatusAttributes}
          completedTxs={completedTxs}
          ongoingTxs={ongoingTxs}
          futureTxs={futureTxs}
          overdueTxs={overdueTxs}
          notPaidServiceFeesTxs={notPaidServiceFeesTxs}
        >
          {txRoleIsCustomer(ownRole) && status === 'pending-payment' &&
            <NamedLink
              name="PaymentPage"
              params={{ transactionId: transaction.id.uuid }} className={css.paymentLink}>
              <FormattedMessage id="InboxPage.pay" />
            </NamedLink>
          }
        </OrderDetails>
        <div className={css.actionButtons}>
          {medicoverPartnership &&
            <div className={classNames(css.medicoverContainer, { [css.orderMedicoverContainer]: !isProvider })}>
              <LogoMedicoverSport />
            </div>}
          {
            userWarning &&
            <div className={css.warningContainer}>
              {!!overdueTxs.length && <div className={css.strongWarning} onClick={() => openTransactionsModal('overdue')}><div className={css.icon}>!</div><div className={css.txsMessage}><FormattedMessage id="TransactionPanel.overdueTxs" /></div></div>}
              {!!notPaidServiceFeesTxs.length && <div className={css.strongWarning} onClick={() => openTransactionsModal('serviceFees')}><div className={css.icon}>!</div><div className={css.txsMessage}>Klient posiada nieopłacone opłaty serwisowe</div></div>}
              {!!ongoingTxs.length && <div className={css.lightWarning} onClick={() => openTransactionsModal('ongoing')}><div className={css.icon}>!</div><div className={css.txsMessage}><FormattedMessage id="TransactionPanel.ongoingTxs" /></div></div>}
            </div>
          }
          {(isAdmin || isLocalAdmin) && isProvider && (
            <div className={css.stolenSection}>
              <InlineTextButton
                disabled={markAsStolenInProgress}
                onClick={() => onUpdateStolenStatus(transaction.id, !transaction.attributes.stolen)}
                className={classNames(css.stolenBtn, { [css.isStolen]: transaction.attributes.stolen })}
              >
                {markAsStolenInProgress ? (
                  <FormattedMessage id="TransactionPanel.updatingStolenStatus" defaultMessage="Aktualizacja..." />
                ) : transaction.attributes.stolen ? (
                  <span className={css.stolenAlert}>⚠️ <FormattedMessage id="TransactionPanel.markAsUnstolen" defaultMessage="Oznacz jako odzyskane" /></span>
                ) : (
                  <FormattedMessage id="TransactionPanel.markAsStolen" defaultMessage="Oznacz jako przywłaczenie" />
                )}
              </InlineTextButton>
              {markAsStolenError && (
                <p className={css.error}>Błąd podczas aktualizacji statusu kradzieży.</p>
              )}
            </div>
          )}
          <ActionButtons
            showButtons={showSaleButtons}
            acceptInProgress={acceptInProgress}
            declineInProgress={declineInProgress}
            pickupInProgress={pickupInProgress}
            cancelInProgress={cancelInProgress}
            completeNotPickedupInProgress={completeNotPickedupInProgress}
            completeInProgress={completeInProgress}
            acceptSaleError={acceptSaleError}
            declineSaleError={declineSaleError}
            pickupSaleError={pickupSaleError}
            completeSaleError={completeSaleError}
            status={status}
            onAcceptSale={() => onAcceptSale(transaction.id)}
            onDeclineSale={() => onDeclineSale(transaction.id)}
            onPickupSale={() => onPickupSale(transaction.id)}
            onCompleteNotPickedupSale={() => onCompleteNotPickedupSale(transaction.id)}
            onCompleteSale={() => onCompleteSale(transaction.id)}
            onCancelSale={() => onCancelSale(transaction.id)}
            transaction={transaction}
            isAdmin={isAdmin}
            isLocalAdmin={isLocalAdmin}
            changeDeliveryStateInProgress={changeDeliveryStateInProgress}
            onChargeServiceFee={openServiceFeeModal}
            isBeforeEndDateDisplaysServiceFee={isBeforeEndDateDisplaysServiceFee}
          />
          {txIsDelivered(transaction) && !review && txRoleIsCustomer(ownRole) && (
            <InlineTextButton onClick={openRateModal} className={css.button}>
              <FormattedMessage id={`OrderInfo.rate`} values={{ displayName }} />
            </InlineTextButton>
          )}
          {
            isExtendRentalBtn && (
              <Button onClick={openProlongationModal} className={css.extendRentalBtn}>
                {intl.formatMessage({ id: 'TransactionPanel.extendRental' })}
              </Button>
            )
          }

          {showHuExternalFormBtn && (
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSebVr0s2RBCqDu0VP3fH2nwG8xkB7H014so_wunKaFWXRiETQ/viewform?usp=header" 
              target="_blank"
              rel="noopener noreferrer"
              className={css.huExternalFormLink}
            >
              <PrimaryButton className={css.huExternalFormBtn}>
                <FormattedMessage id="TransactionPanel.huExternalForm" defaultMessage="Elállás a szerződéstől (felmondás)" />
              </PrimaryButton>
            </a>
          )}

          {
            isCancelByCustomerBtn && (
              <div className={css.customerCancellation}>
                <PrimaryButton
                  className={css.actionButtons}
                  inProgress={cancelInProgress}
                  disabled={false}
                  onClick={() => openPartialCancelModal()}
                >
                  {intl.formatMessage({ id: 'TransactionPanel.cancelRentalByCustomer' })}
                </PrimaryButton>
              </div>
            )
          }
          {isPartialCancelButton && (
            <Button className={css.partialCancelBtn} onClick={openPartialCancelModal}>
              {intl.formatMessage({ id: "TransactionPanelNew.partialCancel" })}
            </Button>
          )}
          {!txRoleIsCustomer(ownRole) && !!deliveryType && isProvider &&
            <div className={css.centralRental}>
              <h4>{intl.formatMessage({ id: "TransactionPanel.centralWarehouse" })}</h4>
              <h5>{`${intl.formatMessage({ id: "TransactionPanel.transportStatus" })}:
                ${computeDeliveryState(deliveryState, deliveryType)}`}</h5>
              <div>
                <SecondaryButton
                  inProgress={changeDeliveryStateInProgress}
                  className={classNames(css.actionButton, css.deliveryButton)}
                  onClick={openDeliveryStateModal}
                >
                  {intl.formatMessage({ id: "TransactionPanel.changingDeliverStatus" })}
                </SecondaryButton>
              </div>
            </div>
          }
          {
            isProtocolButton &&
            <div className={css.protocol}>
              <h3>Protokół zdawczo-odbiorczy</h3>
              {protocolFile && isLessThanOneDay && (
                <a className={css.protocolBtn} href={`${protocolFile.url}`} target="_blank"><FormattedMessage id={'OrderInfo.downloadProtocol'} /></a>
              )}
              {
                (
                  transaction?.provider.attributes.isDecathlon && transaction.provider.attributes.countryId === 'pl'
                ) && (
                  <>
                    <ProtocolState transaction={transaction} onUpdateTransactionOnly={onUpdateTransactionOnly} />
                    {
                      !transaction.attributes.protocol.pickupSigned && transaction.attributes.processState !== 'delivered' &&
                      <Button className={css.peselBtn} onClick={() => openPeselModal('pickup')}>
                        {intl.formatMessage({ id: "TransactionPanel.fillPickupProtocol" })}
                      </Button>
                    }
                    {
                      transaction.attributes.protocol.pickupSigned && transaction.attributes.processState !== 'accepted' &&
                      !transaction.attributes.protocol.returnSigned &&
                      <Button className={css.peselBtn} onClick={() => openPeselModal('deliver')}>
                        {intl.formatMessage({ id: "TransactionPanel.fillDeliveryProtocol" })}
                      </Button>
                    }
                  </>
                )
              }
            </div>
          }
        </div>
      </div>
      <OrderItemInfo
        transaction={transaction}
        deliveryToProvider={transaction.deliveryToProvider}
        provider={transaction.provider}
        listings={transaction.listings}
        intl={intl}
        isAdminOrProvider={currentUser.attributes.isAdmin || isProvider}
        isProvider={isProvider}
        isAdmin={isAdmin}
        onOpenModal={() => setIsUpdateReceiverModalOpen(true)}
      />
      <UpdateReceiverModal
        id="UpdateReceiverModal"
        isOpen={isUpdateReceiverModalOpen}
        onCloseModal={() => setIsUpdateReceiverModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        initialReceiverName={transaction.attributes.receiverName}
        isCustomer={txRoleIsCustomer(ownRole)}
        onSubmit={(receiverName, receiverReason) => {
          return onUpdateReceiver({ id: transaction.id.uuid, receiverName, reason: receiverReason })
            .then(() => setIsUpdateReceiverModalOpen(false))
            .catch(e => console.error('Błąd zmiany odbiorcy', e))
        }
        }
      />
      <TransactionsModal
        isOpen={isTransactionsModalOpen}
        onCloseModal={closeTransactionsModal}
        onManageDisableScrolling={onManageDisableScrolling}
        completedTxs={completedTxs}
        ongoingTxs={ongoingTxs}
        futureTxs={futureTxs}
        overdueTxs={overdueTxs}
        notPaidServiceFeesTxs={notPaidServiceFeesTxs}
        txType={txType}
      />
      <ReviewModal
        id="ReviewOrderModal"
        isOpen={isReviewModalOpen}
        onCloseModal={closeRateModal}
        onManageDisableScrolling={onManageDisableScrolling}
        onSubmitReview={onSubmitReview}
        revieweeName={displayName}
        reviewSent={reviewSubmitted}
        sendReviewInProgress={sendReviewInProgress}
        sendReviewError={sendReviewError}
      />
      <PartialCancelModal
        id="PartialCancelModal"
        isOpen={isPartialCancelModalOpen}
        onCloseModal={closePartialCancelModal}
        onManageDisableScrolling={onManageDisableScrolling}
        partialCancelInProgress={partialCancelInProgress}
        partialCancelError={partialCancelError}
        onPartialCancelOrder={onPartialCancelOrder}
        onCancelSale={() => onCancelSale(transaction.id)}
        cancelInProgress={cancelInProgress}
        transaction={transaction}
        partialCancelSuccess={partialCancelSuccess}
        actualOrderedProducts={actualOrderedProducts}
        cancellationByCustomer={!!isCancelByCustomerBtn}
      />
      <DeliveryStateModal
        id="DeliveryStateModal"
        onCloseModal={closeDeliveryStateModal}
        isOpen={isDeliveryStateModalOpen}
        onManageDisableScrolling={onManageDisableScrolling}
        onChangeDeliveryState={(deliveryState) => onChangeDeliveryState(transaction.id, deliveryState)}
        initialDeliveryState={transaction.attributes.deliveryState}
        isHome={transaction.attributes.deliveryType === 'home'}
      />
      {
        isProvider && isChargeServiceFeeModalOpen &&
        <ServiceFeeModal
          id="SefviceFeeModal"
          onCloseModal={closeServiceFeeModal}
          isOpen={isChargeServiceFeeModalOpen}
          onManageDisableScrolling={onManageDisableScrolling}
          transaction={transaction}
          createServiceFeeInProgress={createServiceFeeInProgress}
          createServiceFeeError={createServiceFeeError}
          createServiceFeeSuccess={createServiceFeeSuccess}
          onCreateServiceFee={onCreateServiceFee}
          onClearServiceFee={onClearServiceFee}
          transactionRole={transactionRole}
        />
      }
      <PickupModal
        isOpen={isPeselModalOpen}
        onClose={closePeselModal}
        onSubmit={onSubmitPeselData}
        listings={listings}
        onManageDisableScrolling={onManageDisableScrolling}
        handleSignProtocol={handleSignProtocol}
        protocolType={protocolType}
        txId={transaction.id.uuid}
        protocol={transaction.attributes.protocol}
        intl={intl}
        signProtocolInProgress={signProtocolInProgress}
        signProtocolSuccess={signProtocolSuccess}
        signProtocolError={signProtocolError}
        duplicatedPeselTransactions={duplicatedPeselTransactions}
        transaction={transaction}
      />
      {
        txRoleIsCustomer(ownRole) && canBeProlongedTo && canBeProlongedTo.length > 0 &&
        <ProlongationModal
          id="ProlongationModal"
          onCloseModal={closeProlongationModalOpen}
          isOpen={isProlongationModalOpen}
          onManageDisableScrolling={onManageDisableScrolling}
          canBeProlongedTo={canBeProlongedTo}
          transactionId={transaction.id.uuid}
          callSetInitialValues={callSetInitialValues}
          onInitializeCardPaymentData={onInitializeCardPaymentData}
          listings={listings}
          provider={provider}
          transaction={transaction}
          actualOrderedProducts={actualOrderedProducts}
        />
      }
      <div className={css.messagesSection}>
        <SendMessageSection
          className={css.sendMessageContainer}
          transaction={transaction}
          sendMessageInProgress={sendMessageInProgress}
          sendMessageError={sendMessageError}
          onSendMessage={onSendMessage}
          intl={intl}
          currentUser={currentUser}
        />
        <FeedSection
          rootClassName={css.feedContainer}
          currentTransaction={transaction}
          currentUser={currentUser}
          fetchMessagesError={fetchMessagesError}
          fetchMessagesInProgress={fetchMessagesInProgress}
          initialMessageFailed={initialMessageFailed}
          messages={messages}
          review={review}
          oldestMessagePageFetched={oldestMessagePageFetched}
          onOpenReviewModal={openRateModal}
          onShowMoreMessages={() => onShowMoreMessages(transaction.id)}
          totalMessagePages={totalMessagePages}
          isProvider={isProvider}
          onUpdateTransaction={onUpdateTransaction}
        />
        {isRelatedTransactions &&
          <RelatedTransactionsSection
            transaction={transaction}
            intl={intl}
            history={history}
            containerClassName={css.relatedTransactions}
          />
        }
      </div>
    </div>
  );
};

TransactionPanelComponent.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  acceptSaleError: null,
  declineSaleError: null,
  fetchMessagesError: null,
  initialMessageFailed: false,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  sendReviewError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  nextTransitions: null,
};

const TransactionPanel = injectIntl(TransactionPanelComponent);

export default TransactionPanel;